import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space, Table, Select, Typography } from 'antd';
import { ZoomInOutlined } from '@ant-design/icons';

import {
  selectors,
  REGIONS,
  UnderlyingAsset,
  updateSessionUnderlyingAssets,
  retrieveUnderlyingAssets,
  CREATING,
  instrumentTypes,
  listPresets,
  requestInstrumentPricePreview,
  HISTORICAL_SCENARIO,
  clearBenchmarkPreview,
  clearInstrumentPreview,
  YIELDS,
  TBILL,
} from 'state';
import { columnSearchWidget, TextShortener } from 'components';
import { useModal } from 'services';
import { ETFDetailsModal } from 'reusable';
import BenchmarkPreviewModal from './Benchmarks/BenchmarkPreviewModal';
import InstrumentPreviewModal from './InstrumentPreviewModal';
// Keep the classic blueprint selector on the same native eligibility policy as the new picker.
import useFastAssetEligibility from '../../NativeSession/useFastAssetEligibility';
import FastAssetSelectionNotice from '../../NativeSession/FastAssetSelectionNotice';
import { useSessionCatalogue } from '../../../features/assetCatalogue/SessionCatalogue';
import { selectedInstrumentCount } from '../../../features/assetCatalogue/filters';

type AssetTableRow = UnderlyingAsset & {
  sector_display: string;
};

const SessionInstrumentsSelector = () => {
  const dispatch = useDispatch();
  const activeSession = useSelector(selectors.activeSession);
  const fastEligibility = useFastAssetEligibility(activeSession);
  const currencies = useSelector(selectors.currencies);
  const { items, waiting } = useSelector(selectors.underlyingAssetsApi);
  const presets = useSelector(selectors.presets);
  const presetsList = Object.values(presets || {});
  // Native presets cannot bypass the supported-asset table; legacy preset contents are unchanged.
  const selectablePresets = presetsList.filter(preset =>
    (preset.assets ?? []).every(fastEligibility.allows),
  );

  const [DetailModal, showDetails] = useModal(ETFDetailsModal);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const session = useSelector(selectors.activeSession);
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;
  const showPreviewButoon = isHistorical && !session.allow_individual_benchmark;

  const hidePreviewModal = () => {
    setShowPreviewModal(false);
  };

  // this moved to parent container
  // useEffect(() => {
  //   dispatch(retrieveUnderlyingAssets({}));
  // }, [dispatch]);

  useEffect(() => {
    if (presetsList.length) return;

    dispatch(listPresets({}));
  }, [dispatch, presetsList.length]);

  useEffect(() => {
    if (session.instrument_preview) {
      setPreviewLoading(false);
      setShowPreviewModal(true);
    } else {
      setShowPreviewModal(false);
    }
  }, [session.instrument_preview]);
  useEffect(() => {
    // Cleanup function
    return () => {
      dispatch(clearInstrumentPreview({}));
    };
  }, []);

  const initialSelected = activeSession.underlying_assets ?? [];
  const [selected, setSelected] = useState(initialSelected);
  const catalogue = useSessionCatalogue(
    activeSession.status !== CREATING ? activeSession.id : undefined,
    activeSession.underlying_assets,
  );
  const instrumentCount = useMemo(
    () => selectedInstrumentCount(selected, catalogue.data?.assets ?? []),
    [selected, catalogue.data],
  );
  console.log(`SELECTED: ${selected}`);

  useEffect(() => {
    setSelected(activeSession.underlying_assets);
  }, [activeSession.underlying_assets]);

  const isDisabled = activeSession.status !== CREATING;
  // Await current eligibility before allowing a native edit or save.
  const editingDisabled = isDisabled || !fastEligibility.ready;
  const showSectorColumn = activeSession.per_sector_alpha;

  const dataSource = useMemo(() => {
    let assets = Object.values(items ?? {}).filter(asset =>
      fastEligibility.allows(asset.id),
    );
    if (isDisabled) {
      assets = assets.filter(u => selected.includes(u.id));
    } else {
      assets = assets.filter(
        u => u.resourcetype !== 'TBill' && u.resourcetype !== 'Yields'
      );
    }
    return assets.map(asset => ({
      ...asset,
      sector_display: asset.asset_sector?.trim() || '-',
    })) as AssetTableRow[];
  }, [items, isDisabled, selected, fastEligibility.allows]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      fixed: 'left' as const,
      render: function Name(text: string, record: AssetTableRow) {
        if (!record.resourcetype.includes('ETF')) return text;

        return (
          <Space>
            <Button
              onClick={() => {
                showDetails({ assetId: record.id });
              }}
            >
              <ZoomInOutlined />
            </Button>
            {TextShortener({ text, maxLength: 20 })}
          </Space>
        );
      },
      sorter: (a: AssetTableRow, b: AssetTableRow) =>
        a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
      ...columnSearchWidget({ dataIndex: 'name' }),
      onCell: (record: AssetTableRow) => {
        return {
          onClick: () => {
            if (activeSession.scenario_type === HISTORICAL_SCENARIO) {
              dispatch(requestInstrumentPricePreview({ asset_id: record.id }));
            }
          },
        };
      },
    },
    {
      title: 'BBG Ticker',
      dataIndex: 'ticker',
      sorter: (a: AssetTableRow, b: AssetTableRow) =>
        a.ticker < b.ticker ? -1 : a.ticker > b.ticker ? 1 : 0,
      ...columnSearchWidget({ dataIndex: 'ticker' }),
    },
    {
      title: 'CCY',
      dataIndex: 'base_currency',
      render(value: number) {
        return currencies[value].symbol;
      },
      filters: Object.entries(currencies).map(([id, currency]) => {
        return {
          text: currency.symbol,
          value: parseInt(id),
        };
      }),
      onFilter: (value: boolean | React.Key, record: AssetTableRow) => {
        return value === record.base_currency;
      },
      sorter: (a: AssetTableRow, b: AssetTableRow) => {
        const currencyA = currencies[a.base_currency]?.symbol ?? '';
        const currencyB = currencies[b.base_currency]?.symbol ?? '';
        return currencyA.localeCompare(currencyB);
      },
    },
    {
      title: 'Region',
      dataIndex: 'region',
      render(value: keyof typeof REGIONS) {
        return REGIONS[value];
      },
      filters: Object.entries(REGIONS).map(([id, region]) => {
        return {
          text: region,
          value: parseInt(id),
        };
      }),
      onFilter: (value: boolean | React.Key, record: AssetTableRow) => {
        return value === record.region;
      },
      sorter: (a: AssetTableRow, b: AssetTableRow) => a.region - b.region,
    },
    {
      title: 'Geography',
      dataIndex: 'geography',
      filters: Array.from(new Set(dataSource.map(asset => asset.geography)))
        .sort((a, b) => (a || '').localeCompare(b || ''))
        .map(geography => ({ text: geography, value: geography })),
      onFilter: (value: boolean | React.Key, record: AssetTableRow) => {
        return value === record.geography;
      },
      sorter: (a: AssetTableRow, b: AssetTableRow) =>
        (a.geography || '').localeCompare(b.geography || ''),
    },
    ...(showSectorColumn
      ? [
          {
            title: 'Sector',
            dataIndex: 'sector_display',
            render(value: string) {
              return value;
            },
            filters: Array.from(
              new Set(dataSource.map(asset => asset.sector_display))
            )
              .sort((a, b) => a.localeCompare(b))
              .map(sector => ({ text: sector, value: sector })),
            filterSearch: true,
            onFilter: (value: boolean | React.Key, record: AssetTableRow) => {
              return value === record.sector_display;
            },
            sorter: (a: AssetTableRow, b: AssetTableRow) =>
              a.sector_display.localeCompare(b.sector_display),
          },
        ]
      : []),
    {
      title: 'Asset Class',
      dataIndex: 'resourcetype',
      fixed: 'right' as const,
      render: (assetClass: string) => {
        return instrumentTypes.find(obj => obj.type === assetClass)?.name;
      },
      filters: instrumentTypes
        .filter(type => type.assetClass !== YIELDS && type.assetClass !== TBILL)
        .map(obj => ({
          text: obj.name,
          value: obj.type,
        })),
      onFilter: (value: boolean | React.Key, record: AssetTableRow) => {
        return value === record.resourcetype;
      },
      sorter: (a: AssetTableRow, b: AssetTableRow) =>
        a.resourcetype.localeCompare(b.resourcetype),
      defaultSortOrder: 'ascend' as const,
    },
  ];

  return (
    // <Space direction="vertical" size={15}>
    <div>
      <FastAssetSelectionNotice
        eligibility={fastEligibility}
        selected={selected}
        disabled={editingDisabled}
        onRemove={ids =>
          setSelected(current => current.filter(id => !ids.includes(id)))
        }
      />
      <Space size={15}>
        {/*<Typography.Title level={3}>Session Instruments</Typography.Title>*/}

        {!isDisabled && (
          <Space size={15} style={{ marginBottom: 9 }}>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select Preset"
              disabled={editingDisabled}
              optionFilterProp="children"
              onChange={value => {
                const id = Number(value);
                setSelected(presets[id]?.assets || []);
              }}
            >
              {selectablePresets.map(preset => {
                const { id, name } = preset;
                return (
                  <Select.Option key={id} value={id}>
                    {name}
                  </Select.Option>
                );
              })}
            </Select>

            <Button
              type="primary"
              disabled={editingDisabled}
              onClick={() =>
                dispatch(
                  updateSessionUnderlyingAssets({
                    session_id: activeSession.id,
                    underlying_assets: selected,
                  }),
                )
              }
            >
              Save
            </Button>
          </Space>
        )}
        {selected.length > 0 && (
          <Typography.Text type={'secondary'}>
            <div style={{ marginBottom: 9 }}>
              ({!catalogue.data && catalogue.loading ? '…' : instrumentCount}{' '}
              instruments selected)
            </div>
          </Typography.Text>
        )}
      </Space>
      <Table
        // sticky={true}
        // style={{ marginTop: 20 }}
        rowKey="id"
        bordered
        loading={waiting || fastEligibility.loading}
        dataSource={dataSource}
        scroll={{ x: true }}
        // scroll: {{x: true}}
        pagination={{
          defaultPageSize: 20,
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selected,
          onChange: (selectedRowKeys: React.Key[]) => {
            setSelected(selectedRowKeys as number[]);
          },
          getCheckboxProps: () => ({
            disabled: editingDisabled,
          }),
        }}
        columns={columns}
      />
      {DetailModal}
      {showPreviewModal && (
        <InstrumentPreviewModal
          visible={showPreviewModal}
          hide={hidePreviewModal}
        />
      )}
    </div>
    // </Space>
  );
};

export default SessionInstrumentsSelector;
